<script setup>
import { computed } from 'vue';

const props = defineProps({
    modelValue: { type: Object, default: () => ({ fields: [] }) },
    errors: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue', 'touched']);

const v = computed({
    get: () => props.modelValue || { fields: [] },
    set: (val) => emit('update:modelValue', val),
});

const fieldOptions = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'sin', label: 'SIN' },
    { key: 'dob', label: 'DOB' },
    { key: 'gross_income', label: 'Gross Income' },
    { key: 'submitted_other_income', label: 'Submitted Other Income' },
    { key: 'employer_name', label: 'Employer Name' },
    { key: 'occupation', label: 'Occupation' },
    { key: 'city', label: 'City' },
    { key: 'province', label: 'Province' },
    { key: 'postal_code', label: 'Postal Code' },
];

const selected = computed({
    get: () => (Array.isArray(v.value.fields) ? v.value.fields : []),
    set: (arr) => {
        v.value = { ...v.value, fields: Array.isArray(arr) ? arr : [] };
    },
});

const isSelected = (key) => selected.value.includes(key);

const setSelected = (key, checked) => {
    emit('touched');
    const cur = new Set(selected.value);
    if (checked) {
        cur.add(key);
    } else {
        cur.delete(key);
    }
    selected.value = Array.from(cur);
};

const modelFor = (key) =>
    computed({
        get: () => isSelected(key),
        set: (checked) => setSelected(key, !!checked),
    });

const hasError = computed(() => Array.isArray(props.errors) && props.errors.length > 0);
</script>

<template>
<div class="w-full" :class="{ 'v-input--error': hasError }">
    <p class="font-semibold mb-2">
        {{ $t('label.required_customer_data_fields') }}
    </p>

    <div class="required-fields-grid">
        <StyledCheckbox
            v-for="opt in fieldOptions"
            :key="opt.key"
            v-model="modelFor(opt.key).value"
            :name="`required_fields.${opt.key}`"
            :label="opt.label"
            hide-details
            density="compact" />
    </div>

    <div
        v-if="hasError"
        class="v-input__details mt-2"
        role="alert"
        aria-live="polite">
        <div class="v-messages">
            <div
                v-for="err in errors"
                :key="err"
                class="v-messages__message">
                {{ err }}
            </div>
        </div>
    </div>
</div>
</template>

<style scoped>
.required-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
}

@media (min-width: 768px) {
    .required-fields-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
        column-gap: 18px;
        row-gap: 6px;
    }
}

.required-fields-grid :deep(.v-input) {
    margin: 0;
}

.required-fields-grid :deep(.v-selection-control) {
    min-height: 0;
}

.required-fields-grid :deep(.v-input__details) {
    display: none;
}
</style>
